import {
	ReactElement,
	useEffect,
	useState
} from "react";
import {
	AlertBar,
	Dialog,
	FormattedText,
	Icon,
	Toolbar
} from "client/Components";
import { useDataInputDialog } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import {
	CompressedImage,
	ImageDimensions
} from "client/Misc/imageUtils";
import styles from "./CompressImageDialog.module.less";

export type CompressImageDialogProps = {
	originalFile: File;
	image: CompressedImage;
	maxFileSize: number;
	onConfirmation: () => void;
	onCancellation: () => void;
};

export const CompressImageDialog = (props: CompressImageDialogProps): ReactElement => {
	const {
		originalFile,
		image,
		maxFileSize,
		onConfirmation,
		onCancellation
	} = props;

	const originalUrl: string | null = useObjectUrl(originalFile);
	const compressedUrl: string | null = useObjectUrl(image.file);

	return (
		<Dialog trackingId="compress-image-dialog">
			<Dialog.Header title={localize("_MakeThisImageSmaller")} />
			<Dialog.Body>
				<p className={styles.info}>
					<FormattedText
						text={localize("_MakeImageSmallerInfo")}
						thingsToReplace={[
							{
								what: "{{FileName}}",
								filler: originalFile.name
							},
							{
								what: "{{MaxSize}}",
								filler: getDisplayFriendlySize(maxFileSize)
							}
						]}
					/>
				</p>

				<div className={styles.comparison}>
					<ImageVersion
						label={localize("_Original")}
						imageUrl={originalUrl}
						dimensions={image.dimensions.from}
						fileSize={originalFile.size}
					/>

					<span className={styles.arrow}>
						<Icon value="arrow-right" />
					</span>

					<ImageVersion
						label={localize("_Smaller")}
						imageUrl={compressedUrl}
						dimensions={image.dimensions.to}
						fileSize={image.file.size}
					/>
				</div>

				{image.didRemoveTransparency && (
					<AlertBar
						kind="warning"
						isInline
						isDismissable={false}
						trackingId="transparency-warning"
					>
						{localize("_TheTransparentPartsOfThisImageBecomeWhite")}
					</AlertBar>
				)}

				<p className={styles.footnote}>
					{localize("_TheFileOnYourDeviceDoesNotChange")}
				</p>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={onConfirmation}
						text={localize("_MakeSmaller")}
						trackingId="confirm"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onCancellation}
						text={localize("_UseOriginal")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
CompressImageDialog.displayName = "CompressImageDialog";

export type CompressImageDialogData = Omit<
	CompressImageDialogProps,
	"onConfirmation" | "onCancellation"
>;

export function useCompressImageDialog(): [
	element: ReactElement | null,
	open: (data: CompressImageDialogData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(data, onCancellation, onConfirmation) => (
			<CompressImageDialog
				{...data}
				onConfirmation={onConfirmation}
				onCancellation={onCancellation}
			/>
		)
	);
}

type ImageVersionProps = {
	label: string;
	imageUrl: string | null;
	dimensions: ImageDimensions;
	fileSize: number;
};

const ImageVersion = (props: ImageVersionProps): ReactElement => {
	const {
		label,
		imageUrl,
		dimensions,
		fileSize
	} = props;

	return (
		<div className={styles.version}>
			<span className={styles.label}>{label}</span>

			<div className={styles.thumbnail}>
				{imageUrl !== null && (
					<img
						className={styles.image}
						src={imageUrl}
						alt={label}
					/>
				)}
			</div>

			<span className={styles.dimensions}>
				{formatDimensions(dimensions)}
			</span>

			<span className={styles.fileSize}>
				{getDisplayFriendlySize(fileSize)}
			</span>
		</div>
	);
};
ImageVersion.displayName = "CompressImageDialog.ImageVersion";

function useObjectUrl(file: File): string | null {
	const [objectUrl, setObjectUrl] = useState<string | null>(null);

	useEffect(
		(): (() => void) => {
			const url: string = URL.createObjectURL(file);
			setObjectUrl(url);

			return (): void => {
				URL.revokeObjectURL(url);
				setObjectUrl(null);
			};
		},
		[file]
	);

	return objectUrl;
}

function formatDimensions(dimensions: ImageDimensions): string {
	return `${dimensions.width} × ${dimensions.height}`;
}
